import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  Archive,
  Building2,
  Layers,
  MapPin,
  Pencil,
  Plus,
} from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { GatedButton } from '@/components/ui/GatedButton';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { ApiErrorAlert } from '@/components/ui/ApiErrorAlert';
import { EmptyState, Skeleton, Spinner } from '@/components/ui/Feedback';
import { OrganizationFormDialog } from './OrganizationFormDialog';
import { SiteFormDialog } from './SiteFormDialog';
import { DeviceGroupFormDialog } from './DeviceGroupFormDialog';
import { groupsForOrganization, sitesForOrganization } from './hierarchy';
import {
  archiveDeviceGroup,
  archiveOrganization,
  archiveSite,
  listDeviceGroups,
  listOrganizations,
  listSites,
} from '@/lib/api/endpoints/organizations';
import { useAuth } from '@/lib/auth/AuthProvider';
import { queryKeys } from '@/lib/query-keys';
import { formatNumber } from '@/lib/format';
import type { DeviceGroup, Organization, OrganizationStatus, Site } from '@/lib/api/types';

/*
  Organizations, with their sites and device groups nested underneath, and the create, edit and
  archive controls for all three.

  The hierarchy shown here is the same one the authorization model scopes against
  (docs/07-identity-rbac.md I.2: organization, site, device_group, device), so a technician
  reading this page is reading the shape their own assignments are cut from.

  Archive rather than delete, everywhere. The schema carries deleted_at rather than supporting a
  hard delete, and it has to: devices, sessions and audit rows reference these records, and the
  audit log is append-only by design. An archive that refused nothing would also be a footgun,
  so the API refuses to archive a record that still has children, and the confirmation copy says
  which children and what to do about them.

  Every write needs organization.manage, which is [dangerous] in the catalog. An account without
  it gets the controls as GatedButtons naming the permission rather than a page with the buttons
  missing, so the absence is legible.
*/

const STATUS_TONE: Readonly<Record<OrganizationStatus, 'success' | 'warning' | 'neutral'>> = {
  active: 'success',
  suspended: 'warning',
  archived: 'neutral',
};

/** Which dialog is open, and on what. One state rather than six booleans. */
type DialogState =
  | { readonly kind: 'none' }
  | { readonly kind: 'organization'; readonly organization: Organization | null }
  | { readonly kind: 'site'; readonly organization: Organization; readonly site: Site | null }
  | {
      readonly kind: 'group';
      readonly organization: Organization;
      readonly group: DeviceGroup | null;
    }
  | { readonly kind: 'archiveOrganization'; readonly organization: Organization }
  | { readonly kind: 'archiveSite'; readonly site: Site }
  | { readonly kind: 'archiveGroup'; readonly group: DeviceGroup };

const MANAGE_REASON =
  'Creating, editing and archiving customers, sites and groups needs the organization.manage permission, which none of your role assignments grant. It is marked dangerous in the permission catalog, so it is not given out by default.';

export function OrganizationsPage(): React.JSX.Element {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<DialogState>({ kind: 'none' });

  const organizations = useQuery({
    queryKey: queryKeys.organizations,
    queryFn: listOrganizations,
  });
  const sites = useQuery({ queryKey: queryKeys.sites(), queryFn: () => listSites() });
  const groups = useQuery({
    queryKey: queryKeys.deviceGroups(),
    queryFn: () => listDeviceGroups(),
  });

  const manage = can('organization.manage');
  const loading = organizations.isPending || sites.isPending || groups.isPending;

  const invalidateAll = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['organizations'] });
    void queryClient.invalidateQueries({ queryKey: ['sites'] });
    void queryClient.invalidateQueries({ queryKey: ['deviceGroups'] });
    void queryClient.invalidateQueries({ queryKey: ['devices'] });
  };

  const archiveOrg = useMutation({
    mutationFn: (organizationId: string) => archiveOrganization(organizationId),
    onSuccess: invalidateAll,
  });
  const archiveOneSite = useMutation({
    mutationFn: (siteId: string) => archiveSite(siteId),
    onSuccess: invalidateAll,
  });
  const archiveOneGroup = useMutation({
    mutationFn: (groupId: string) => archiveDeviceGroup(groupId),
    onSuccess: invalidateAll,
  });

  function close(): void {
    setDialog({ kind: 'none' });
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Organizations"
        description="Customer companies, their sites and their device groups. This is the same hierarchy that scopes every role assignment."
        actions={
          manage ? (
            <Button
              variant="primary"
              onClick={() => {
                setDialog({ kind: 'organization', organization: null });
              }}
            >
              <Plus className="size-4" aria-hidden="true" />
              New organization
            </Button>
          ) : (
            <GatedButton reason={MANAGE_REASON}>
              <Plus className="size-4" aria-hidden="true" />
              New organization
            </GatedButton>
          )
        }
      />

      {organizations.isError ? (
        <ApiErrorAlert
          error={organizations.error}
          context="Could not load organizations"
          onRetry={() => {
            void organizations.refetch();
          }}
        />
      ) : null}
      {sites.isError ? (
        <ApiErrorAlert
          error={sites.error}
          context="Could not load sites"
          onRetry={() => {
            void sites.refetch();
          }}
        />
      ) : null}
      {groups.isError ? (
        <ApiErrorAlert
          error={groups.error}
          context="Could not load device groups"
          onRetry={() => {
            void groups.refetch();
          }}
        />
      ) : null}

      {loading ? (
        <div className="flex flex-col gap-4">
          <Spinner label="Loading organizations" />
          {[0, 1, 2].map((index) => (
            <Card key={index} className="p-4">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="mt-3 h-20 w-full" />
            </Card>
          ))}
        </div>
      ) : (organizations.data ?? []).length === 0 ? (
        <Card>
          <EmptyState
            icon={<Building2 className="size-5" aria-hidden="true" />}
            title="No organizations yet"
            description="An organization is a customer company. Create one before minting an enrollment token, because every device belongs to exactly one organization."
            action={
              manage ? (
                <Button
                  variant="primary"
                  onClick={() => {
                    setDialog({ kind: 'organization', organization: null });
                  }}
                >
                  <Plus className="size-4" aria-hidden="true" />
                  New organization
                </Button>
              ) : undefined
            }
          />
        </Card>
      ) : (
        <ul className="grid gap-4 xl:grid-cols-2">
          {(organizations.data ?? []).map((org) => {
            const orgSites = sitesForOrganization(sites.data ?? [], org.id);
            const orgGroups = groupsForOrganization(groups.data ?? [], org.id);
            return (
              <li key={org.id}>
                <Card className="h-full">
                  <CardHeader
                    title={
                      <span className="flex items-center gap-2">
                        {org.name}
                        <Badge tone={STATUS_TONE[org.status]}>{org.status}</Badge>
                      </span>
                    }
                    description={
                      org.externalRef === null
                        ? `Slug ${org.slug}`
                        : `Slug ${org.slug} - PSA reference ${org.externalRef}`
                    }
                    action={
                      <div className="flex items-center gap-1">
                        <Link
                          to="/devices"
                          search={{ orgs: org.id }}
                          className="rounded px-1.5 text-xs font-semibold text-accent hover:underline"
                        >
                          Devices
                        </Link>
                        {manage ? (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={`Edit ${org.name}`}
                              onClick={() => {
                                setDialog({ kind: 'organization', organization: org });
                              }}
                            >
                              <Pencil className="size-3.5" aria-hidden="true" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={`Archive ${org.name}`}
                              disabled={org.status === 'archived'}
                              title={
                                org.status === 'archived'
                                  ? 'Already archived.'
                                  : 'Archive this customer.'
                              }
                              onClick={() => {
                                setDialog({ kind: 'archiveOrganization', organization: org });
                              }}
                            >
                              <Archive className="size-3.5" aria-hidden="true" />
                            </Button>
                          </>
                        ) : null}
                      </div>
                    }
                  />

                  <div className="grid gap-0 sm:grid-cols-2">
                    <section
                      aria-label={`Sites at ${org.name}`}
                      className="border-border p-4 sm:border-r"
                    >
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <h3 className="flex items-center gap-1.5 text-xs font-semibold tracking-wide text-fg-muted uppercase">
                          <MapPin className="size-3.5" aria-hidden="true" />
                          Sites ({formatNumber(orgSites.length)})
                        </h3>
                        {manage ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={`Add a site to ${org.name}`}
                            onClick={() => {
                              setDialog({ kind: 'site', organization: org, site: null });
                            }}
                          >
                            <Plus className="size-3.5" aria-hidden="true" />
                            Add
                          </Button>
                        ) : null}
                      </div>
                      {orgSites.length === 0 ? (
                        <p className="text-xs text-fg-subtle">No sites defined.</p>
                      ) : (
                        <ul className="flex flex-col gap-1.5">
                          {orgSites.map((site) => {
                            return (
                              <li key={site.id} className="flex items-start justify-between gap-2">
                                <span className="min-w-0 text-sm">
                                  <span className="text-fg">{site.name}</span>
                                  <span className="block text-xs text-fg-subtle">
                                    {[site.city, site.region]
                                      .filter((part) => part !== null)
                                      .join(', ')}
                                    {' - '}
                                    {site.timezone}
                                  </span>
                                </span>
                                {manage ? (
                                  <span className="flex shrink-0 items-center">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      aria-label={`Edit site ${site.name}`}
                                      onClick={() => {
                                        setDialog({ kind: 'site', organization: org, site });
                                      }}
                                    >
                                      <Pencil className="size-3" aria-hidden="true" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      aria-label={`Archive site ${site.name}`}
                                      onClick={() => {
                                        setDialog({ kind: 'archiveSite', site });
                                      }}
                                    >
                                      <Archive className="size-3" aria-hidden="true" />
                                    </Button>
                                  </span>
                                ) : null}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </section>

                    <section aria-label={`Device groups at ${org.name}`} className="p-4">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <h3 className="flex items-center gap-1.5 text-xs font-semibold tracking-wide text-fg-muted uppercase">
                          <Layers className="size-3.5" aria-hidden="true" />
                          Device groups ({formatNumber(orgGroups.length)})
                        </h3>
                        {manage ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={`Add a device group to ${org.name}`}
                            onClick={() => {
                              setDialog({ kind: 'group', organization: org, group: null });
                            }}
                          >
                            <Plus className="size-3.5" aria-hidden="true" />
                            Add
                          </Button>
                        ) : null}
                      </div>
                      {orgGroups.length === 0 ? (
                        <p className="text-xs text-fg-subtle">No groups defined.</p>
                      ) : (
                        <ul className="flex flex-col gap-1.5">
                          {orgGroups.map((group) => {
                            const site = orgSites.find((candidate) => candidate.id === group.siteId);
                            return (
                              <li key={group.id} className="flex items-start justify-between gap-2">
                                <span className="min-w-0 text-sm">
                                  <span className="text-fg">{group.name}</span>
                                  <span className="block text-xs text-fg-subtle">
                                    {site === undefined
                                      ? 'Organization wide'
                                      : `Site: ${site.name}`}
                                  </span>
                                </span>
                                {manage ? (
                                  <span className="flex shrink-0 items-center">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      aria-label={`Edit group ${group.name}`}
                                      onClick={() => {
                                        setDialog({ kind: 'group', organization: org, group });
                                      }}
                                    >
                                      <Pencil className="size-3" aria-hidden="true" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      aria-label={`Archive group ${group.name}`}
                                      onClick={() => {
                                        setDialog({ kind: 'archiveGroup', group });
                                      }}
                                    >
                                      <Archive className="size-3" aria-hidden="true" />
                                    </Button>
                                  </span>
                                ) : null}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </section>
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      {/*
        The dialogs are rendered from one state value, and each is keyed by what it is editing so
        a second Edit on a different record mounts a fresh form rather than reusing the previous
        one's state.
      */}
      {dialog.kind === 'organization' ? (
        <OrganizationFormDialog
          key={dialog.organization?.id ?? 'new'}
          open
          onOpenChange={(next) => {
            if (!next) {
              close();
            }
          }}
          organization={dialog.organization}
        />
      ) : null}

      {dialog.kind === 'site' ? (
        <SiteFormDialog
          key={dialog.site?.id ?? `new-${dialog.organization.id}`}
          open
          onOpenChange={(next) => {
            if (!next) {
              close();
            }
          }}
          organization={dialog.organization}
          site={dialog.site}
        />
      ) : null}

      {dialog.kind === 'group' ? (
        <DeviceGroupFormDialog
          key={dialog.group?.id ?? `new-${dialog.organization.id}`}
          open
          onOpenChange={(next) => {
            if (!next) {
              close();
            }
          }}
          organization={dialog.organization}
          group={dialog.group}
        />
      ) : null}

      {dialog.kind === 'archiveOrganization' ? (
        <ConfirmDialog
          open
          onOpenChange={(next) => {
            if (!next) {
              close();
            }
          }}
          title={`Archive ${dialog.organization.name}?`}
          summary={`${dialog.organization.name}, slug ${dialog.organization.slug}. Archiving marks the customer closed.`}
          consequence={
            <>
              <p>
                {dialog.organization.name} stops appearing in the pickers used to place a device or
                mint an enrollment token. Its sites, groups, session history and audit entries are
                all retained, because the audit log is append-only and removing them would break
                it.
              </p>
              <p className="mt-1">
                The API refuses this while the customer still has active devices, so that no
                endpoint is left holding a working credential for a customer that no longer exists.
                Revoke or move them first; the refusal will say how many there are.
              </p>
            </>
          }
          confirmLabel="Archive organization"
          tone="warning"
          onConfirm={() => archiveOrg.mutateAsync(dialog.organization.id)}
        />
      ) : null}

      {dialog.kind === 'archiveSite' ? (
        <ConfirmDialog
          open
          onOpenChange={(next) => {
            if (!next) {
              close();
            }
          }}
          title={`Archive the site ${dialog.site.name}?`}
          summary={`${dialog.site.name}, timezone ${dialog.site.timezone}.`}
          consequence={
            <>
              <p>
                The site stops being offered when placing a device or scoping a role. Any role
                assignment scoped to this site stops granting anything.
              </p>
              <p className="mt-1">
                The API refuses this while devices or device groups still point at it, so nothing
                is left orphaned. Move them first.
              </p>
            </>
          }
          confirmLabel="Archive site"
          tone="warning"
          onConfirm={() => archiveOneSite.mutateAsync(dialog.site.id)}
        />
      ) : null}

      {dialog.kind === 'archiveGroup' ? (
        <ConfirmDialog
          open
          onOpenChange={(next) => {
            if (!next) {
              close();
            }
          }}
          title={`Archive the group ${dialog.group.name}?`}
          summary={dialog.group.name}
          consequence={
            <>
              <p>
                The group stops being offered when placing a device. Any role assignment scoped to
                this group stops granting anything, which is the quiet way a technician loses
                access without anybody editing their assignment.
              </p>
              <p className="mt-1">
                The API refuses this while devices are still in the group. Move them out first.
              </p>
            </>
          }
          confirmLabel="Archive group"
          tone="warning"
          onConfirm={() => archiveOneGroup.mutateAsync(dialog.group.id)}
        />
      ) : null}
    </div>
  );
}
